// Single source of truth for the place-derived visual search radius.
//
// Both the homepage submit (BuscadorDesktop → buildBuscarUrl → URL `radio=`)
// AND the live /Buscar place pick (TopBar LocationInput → setFilters +
// applyPlaceBoundary) compute the visual radius through this helper — so the
// circle is identical whether the user arrives via the homepage searchbar or
// picks within /Buscar. Previously these were duplicated, drifted, and produced
// inconsistent circles for the same place (D15 amend, 2026-06-24).
//
// Returns { rendered, raw }:
//   - `rendered` — the value used everywhere downstream (BoundaryOverlay,
//     backend filter, camera fit). Post-clamp + post-sublocality-cap.
//   - `raw` — the unclamped/uncapped half-diagonal in meters; useful only
//     for the debug overlay so it can render Google's pre-clamp circle as
//     a reference layer. null when viewport is missing.
//
// Algorithm:
//   1. Half-diagonal of viewport in meters (Mercator-corrected for cos(lat)).
//      Fallback 5km when viewport missing.
//   2. Clamp to global [1km, 30km] — defends against degenerate viewports.
//   3. Sublocalities (Mexican colonias) get a 6km ceiling — caps umbrella
//      place names that return oversized viewports (e.g. "Providencia 1a
//      sección"). No floor: D21 grace (min 800m, applied in apiBody) is the
//      safety net against tight viewports.
export const computePlaceRadius = (place, { hasNeighborhood }) => {
    const viewport = place?.geometry?.viewport;
    let raw = null;
    let r = 5000;
    if (viewport) {
        const ne = viewport.getNorthEast();
        const sw = viewport.getSouthWest();
        const latM = (ne.lat() - sw.lat()) * 111_000;
        const cosLat = Math.cos((ne.lat() * Math.PI) / 180);
        const lngM = (ne.lng() - sw.lng()) * 111_000 * cosLat;
        raw = Math.round(Math.sqrt(latM * latM + lngM * lngM) / 2);
        r = raw;
    }
    r = Math.min(Math.max(r, 1000), 30_000);
    const rendered = hasNeighborhood ? Math.min(r, 6000) : r;
    return { rendered, raw };
};
